import { createApp, reactive } from "petite-vue"

type QueryParams = {
    [key: string]: string | string[]
}

interface ArchiveData {
    title: string
    offers: string[]
    totalCount: number
    foundPosts: number
    page: number
    nextPageUrl: string | null
    pager: string
    query: QueryParams
    apiUrl: string
    url: string
    queryOrder: string[]
    filterParams: {
        [key: string]: {
            key: string
            label: string
            type: string
            options?: { [key: string]: string }
            unit?: string
        }
    }
    mapPoints?: any[]
}

declare global {
    const archiveData: ArchiveData
}

type StateFilter = "k-dispozici" | "archiv"

const FIXED_PER_PAGE = 12

interface FilterParamTag {
    key: string
    value: string
    label: string
}

interface ArchiveStore extends ArchiveData {
    appendPosts: boolean
    scrollIntoView: boolean
    isLoading: boolean
    order: string
    search: string
    filteredCount: number
    queryOrder: string[]
    selectedParams: QueryParams

    // Advanced options logic
    showAdvanced: boolean
    toggleAdvanced(): void

    // Dropdown logic
    activeDropdown: string | null
    toggleDropdown(key: string): void
    closeDropdown(): void

    // Address logic
    addressSuggestions: any[]
    addressDebounceTimer: any
    handleAddressInput(event: Event): void
    selectAddress(suggestion: any): void

    // Getters
    readonly selectedParamsTags: FilterParamTag[]

    // Methods
    setQuery(params: any, resetPager?: boolean, resetQuery?: boolean): void
    removeParameter(key: string, value: string): void
    changeState(state: StateFilter): void
    updatePillBackground(): void
    changeOrder(): void
    changePage(page: number): void
    loadNextPage(): void
    openParametersFilter(e: Event): void
    closeParametersFilter(e: Event): void
    handleFilterClosing(): void
    searchOffers(): void
    clearSearch(): void
    filterOffers(): void
    clearFilter(): void
    prepareQueryString(queryParams?: any, type?: string): string
    getArchive(queryParams?: any): Promise<void>
    getSummary(): Promise<void>

    // Helpers
    ui: {
        locality: string
        saleType: string
        estate: string
    }
    clearKey(key: string): void
    clearPrice(key: string): void
}

export default function initArchiveOffer(): void {
    const archiveGrid = document.querySelector("[data-offer-grid]")
    if (!archiveGrid || !archiveData) return

    const queryToSelectedParams = (query: QueryParams, filterParams: any = null) => {
        const currentFilterParams = filterParams || archiveData.filterParams

        return Object.values(currentFilterParams ?? {}).reduce<QueryParams>(
            (params, parameter) => {
                const newParams: any = {}

                if (parameter.type === "range") {
                    ;["od", "do"].forEach((range) => {
                        const key = `${parameter.key}_${range}`
                        newParams[key] = query[key] ?? ""
                    })
                } else {
                    const isSingle = parameter.type !== "check"
                    const queryValue = query[parameter.key] as string

                    newParams[parameter.key] = isSingle
                        ? queryValue ?? ""
                        : queryValue?.split(",") ?? []
                }

                return { ...params, ...newParams }
            },
            {
                stav: (query["stav"] ?? "k-dispozici") as any,
                adresa_text: query["adresa_text"] ?? "",
                lat: (query["lat"] ?? "") as string,
                lng: (query["lng"] ?? "") as string,
                radius: (query["radius"] ?? "") as string,
            }
        )
    }

    const archive: ArchiveStore = reactive({
        ...archiveData,

        appendPosts: false,
        scrollIntoView: false,
        isLoading: false,

        order: (archiveData.query["razeni"] ?? "nejnovejsi") as string,
        search: (archiveData.query["hledat"] ?? "") as string,
        filteredCount: archiveData.totalCount ?? 0,

        // pojistka: aby se "stav" vždy serializoval do URL
        // pojistka: aby se "stav" vždy serializoval do URL
        queryOrder: Array.from(new Set([...(archiveData.queryOrder ?? []), "stav", "adresa_text", "lat", "lng", "radius"])),

        // --- Advanced Options Logic ---
        showAdvanced: false,

        toggleAdvanced() {
            this.showAdvanced = !this.showAdvanced
        },

        // --- Dropdown Logic ---
        activeDropdown: null,

        toggleDropdown(key: string) {
            if (this.activeDropdown === key) {
                this.activeDropdown = null
            } else {
                this.activeDropdown = key
            }
        },

        closeDropdown() {
            this.activeDropdown = null
        },

        selectedParams: queryToSelectedParams(archiveData.query),

        get selectedParamsTags(): FilterParamTag[] {
            const tags: FilterParamTag[] = []

            const baseKeyOf = (key: string) => key.replace(/_(od|do)$/, "")
            const rangePartOf = (key: string) =>
                key.endsWith("_od") ? "od" : key.endsWith("_do") ? "do" : ""

            Object.entries(this.selectedParams).forEach(([key, rawValue]) => {
                const baseKey = baseKeyOf(key)
                const parameter = this.filterParams?.[baseKey]
                if (!parameter) return

                if (parameter.type === "range") {
                    const part = rangePartOf(key)
                    const num = String(rawValue ?? "").trim()
                    if (!part || num === "") return

                    const unit = (parameter.unit ?? "").replace("m2", "m<sup>2</sup>")
                    const formatted = Number(num).toLocaleString()
                    const label = `${part} ${formatted}${unit ? ` ${unit}` : ""}`

                    tags.push({ key, value: num, label })
                    return
                }

                const terms = Array.isArray(rawValue) ? rawValue : [rawValue]
                terms
                    .map((v) => String(v ?? "").trim())
                    .filter((v) => v !== "")
                    .forEach((term) => {
                        const label =
                            parameter.options && parameter.options[term]
                                ? parameter.options[term]
                                : term

                        tags.push({
                            key: baseKey,
                            value: term,
                            label,
                        })
                    })
            })

            return tags
        },

        setQuery(params: any = {}, resetPager = false, resetQuery = false) {
            const query = params
                ? {
                    ...(resetQuery ? {} : this.query),
                    ...params,
                    ...(resetPager ? { strana: 1 } : {}),
                    // ✅ natvrdo vždy 12
                    pocet: FIXED_PER_PAGE,
                }
                : { pocet: FIXED_PER_PAGE }

            // Always ensure sorting and search are present unless explicitly overridden
            if (!query.hasOwnProperty("razeni")) query.razeni = this.order
            if (!query.hasOwnProperty("hledat")) query.hledat = this.search

            this.getArchive(query)
        },

        removeParameter(key: string, value: string) {
            if (!this.selectedParams.hasOwnProperty(key)) return

            const current = (this.selectedParams as any)[key]

            if (Array.isArray(current)) {
                ; (this.selectedParams as any)[key] = current.filter((t: string) => t !== value)
            } else {
                ; (this.selectedParams as any)[key] = ""
            }

            this.setQuery(this.selectedParams, true, true)
        },

        changeState(state: StateFilter) {
            this.selectedParams["stav"] = state as any
            this.page = 1
            this.scrollIntoView = false
            this.setQuery({ stav: state }, true)

            // Update pill background size/position after state change
            setTimeout(() => this.updatePillBackground(), 0)
        },

        updatePillBackground() {
            // Pill position and width are now handled entirely by CSS.
            // The pill is always 50% wide and translateX(0%/100%) is
            // driven by the data-state attribute — no JS calculation needed.
        },

        changeOrder() {
            this.setQuery({ razeni: this.order }, true)
        },

        changePage(page: number) {
            this.scrollIntoView = true
            this.setQuery({ strana: page })
        },

        loadNextPage() {
            this.appendPosts = true
            this.setQuery({ strana: this.page + 1 })
        },

        openParametersFilter(e: Event) {
            const target = e.target as HTMLElement
            const dialog = target?.closest("aside")?.querySelector("dialog")
            if (!dialog) return

            dialog.showModal()

            // Close when clicking backdrop (click outside)
            dialog.onclick = (event) => {
                if (event.target === dialog) {
                    this.closeParametersFilter(event)
                }
            }
        },

        closeParametersFilter(e: Event) {
            const target = e.target as HTMLElement
            target.closest("dialog")?.close()
        },

        handleFilterClosing() {
            if (this.isLoading) return

            this.search = archive.query["hledat"] ?? ""
            this.order = archive.query["razeni"] ?? "nejnovejsi"
            this.selectedParams = queryToSelectedParams(this.query as any, this.filterParams)
            this.filteredCount = this.totalCount
        },

        searchOffers() {
            this.setQuery({ hledat: this.search }, true, true)
        },

        clearSearch() {
            this.search = ""
            this.setQuery({ hledat: "" }, true, true)
        },

        filterOffers() {
            this.setQuery(this.selectedParams, true, true)
        },

        clearFilter() {
            this.selectedParams = queryToSelectedParams({ stav: this.selectedParams["stav"] } as any, this.filterParams)
            this.setQuery(this.selectedParams, true, true)
        },

        prepareQueryString(queryParams: any = {}, type: string = "") {
            const query = new URLSearchParams()

            // ✅ pojistka: i kdyby to přišlo bez pocet, v URL bude vždy 12
            queryParams = { ...queryParams, pocet: FIXED_PER_PAGE }

            this.queryOrder.forEach((param: string) => {
                const value: string | string[] = queryParams[param] ?? ""
                if (!queryParams.hasOwnProperty(param)) return

                query.append(
                    param,
                    Array.isArray(value) ? value.sort().join(",") : String(value)
                )
            })

            query.get("strana") === "1" && query.delete("strana")
            query.get("razeni") === "nejnovejsi" && query.delete("razeni")

                ;[...query.entries()].forEach(([key, value]) => {
                    ; (value === "" || value === "default") && query.delete(key)
                })

            type?.length && query.set("return", type)
            query.set("_t", Date.now().toString())

            return [...query].length ? `?${decodeURIComponent(query.toString())}` : ""
        },

        async getArchive(queryParams: any = {}) {
            this.isLoading = true

            const queryString = this.prepareQueryString(queryParams)
            const targetUrl = `${location.pathname}${queryString}`

            await fetch(`${this.apiUrl}${queryString}`, { method: "GET" })
                .then((response) => response.json())
                .then((data) => {
                    this.offers = [...(this.appendPosts ? this.offers : []), ...data.offers]
                    this.totalCount = this.filteredCount = data.totalCount
                    this.foundPosts = data.foundPosts
                    this.page = data.page
                    this.pager = data.pager
                    this.nextPageUrl = data.nextPageUrl
                    this.filterParams = data.filterParams
                    this.query = data.query
                    this.search = data.query["hledat"] ?? ""
                    this.order = data.query["razeni"] ?? "nejnovejsi"
                    this.selectedParams = queryToSelectedParams(data.query, data.filterParams)

                    document.querySelector("h1")!.innerText = data.title

                    this.scrollIntoView &&
                        !this.appendPosts &&
                        (archiveGrid as HTMLElement)?.scrollIntoView()

                    // Trigger map update
                    if (data.mapPoints) {
                        const mapEl = document.querySelector('.Map[data-map="properties"]')
                        if (mapEl) {
                            mapEl.dispatchEvent(new CustomEvent('map:update-points', {
                                detail: { points: data.mapPoints }
                            }))
                        }
                    }

                    if (!queryParams.hasOwnProperty("noHistory")) {
                        const historyQuery = new URLSearchParams(queryString)
                        historyQuery.delete("_t")
                        if (historyQuery.get("pocet") === "12") historyQuery.delete("pocet")
                        const cleanQueryString = historyQuery.toString()
                        const cleanUrl = `${location.pathname}${cleanQueryString ? '?' + decodeURIComponent(cleanQueryString) : ''}`
                        history.pushState({}, "", cleanUrl)
                    }
                })
                .catch((e) => {
                    if (e.name !== "AbortError") console.error("Archive fetch error:", e)
                })
                .finally(() => {
                    this.isLoading = this.appendPosts = this.scrollIntoView = false
                })
        },

        async getSummary() {
            const queryString = this.prepareQueryString(this.selectedParams, "summary")
            const targetUrl = `${this.apiUrl}${queryString}`

            console.log("Fetching summary:", targetUrl)

            await fetch(targetUrl)
                .then((response) => response.json())
                .then((data) => {
                    this.filteredCount = data.count
                })
        },

        get ui() {
            const getLabel = (key: string, def: string) => {
                const val = this.selectedParams[key]
                if (Array.isArray(val) && val.length) {
                    if (val.length === 1) {
                        // Find label in options
                        const opts = this.filterParams?.[key]?.options ?? {}
                        return opts[val[0]] || val[0]
                    }
                    return `${val.length} vybráno`
                }
                if (typeof val === 'string' && val) {
                    const opts = this.filterParams?.[key]?.options ?? {}
                    return opts[val] || val
                }
                return def
            }

            // Special handling for locality (address or radius)
            let locLabel = this.selectedParams['adresa_text'] || 'Celá nabídka'
            if (this.selectedParams['radius']) {
                const rOpts = { '0.5': '0,5 km', '1': '1 km', '5': '5 km', '10': '10 km', '25': '25 km', '50': '50 km' } as any
                locLabel += ` (+${rOpts[this.selectedParams['radius']] || this.selectedParams['radius'] + ' km'})`
            } else {
                // If checking 'lokalita' param (districts/regions)
                locLabel = getLabel('lokalita', locLabel)
            }

            return {
                locality: locLabel,
                saleType: getLabel('typ_prodeje', 'Vše'),
                estate: getLabel('nemovitost', 'Vše'),
            }
        },

        clearKey(key: string) {
            this.selectedParams[key] = []
            this.filterOffers()
        },
        clearPrice(key: string) {
            this.selectedParams[`${key}_od`] = ''
            this.selectedParams[`${key}_do`] = ''
            this.filterOffers()
        },

        // --- Address Autocomplete Logic ---
        addressSuggestions: [],
        addressDebounceTimer: null,

        handleAddressInput(event: Event) {
            const value = (event.target as HTMLInputElement).value
            this.selectedParams['lat'] = ''
            this.selectedParams['lng'] = ''

            if (this.addressDebounceTimer) clearTimeout(this.addressDebounceTimer)

            if (value.length < 3) {
                this.addressSuggestions = []
                return
            }

            this.addressDebounceTimer = setTimeout(() => {
                fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&countrycodes=cz&addressdetails=1&limit=5`)
                    .then(res => res.json())
                    .then(data => {
                        this.addressSuggestions = data
                    })
            }, 300)
        },

        selectAddress(suggestion: any) {
            this.selectedParams['adresa_text'] = suggestion.display_name
            this.selectedParams['lat'] = suggestion.lat
            this.selectedParams['lng'] = suggestion.lon
            this.addressSuggestions = []
            this.filterOffers()
        }
    })

    const pager = document.querySelector("[data-pager]")
    pager?.querySelector("nav")?.addEventListener("click", (e) => {
        const target = e.target as HTMLElement
        if (!target.matches("a")) return

        e.preventDefault()
        const pageUrl = new URLSearchParams(new URL(target.getAttribute("href")!).search)
        archive.changePage(parseInt(pageUrl.get("strana") ?? "1", 10))
    })

    createApp({ archive }).mount(archiveGrid as HTMLElement)

    // Initialize pill background after mount
    setTimeout(() => archive.updatePillBackground(), 100)

    // Update on window resize
    window.addEventListener('resize', () => archive.updatePillBackground())
}
